import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Navigate } from "react-router-dom";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { CloseX } from "components/utility/CloseX";
import { FrmTextField } from "components/formFields/FrmTextField";

import {
  startBatchSqlUpdate,
  getBatchSqlStatus,
  retryFailedBatchSql,
} from "services/sosInventoryService/sosApi";

// Module-level state to persist across navigation
let persistedExecutionGuid = null;

export function BatchSqlUpdate() {
  const navigate = useNavigate();

  const sysAdmin = useSelector(
    (state) => state.userCompanySettings.supportPrivileges?.sysAdmin
  );

  const [dbUsername, setDbUsername] = useState("");
  const [dbPassword, setDbPassword] = useState("");
  const [sqlScript, setSqlScript] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executionGuid, setExecutionGuid] = useState(persistedExecutionGuid);
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const pollRef = useRef(null);

  function handleClose() {
    navigate("/supportconsole");
  }

  // Resume polling if returning with an active execution
  useEffect(() => {
    if (executionGuid && !pollRef.current) {
      setExecuting(true);
      startPolling(executionGuid);
    }
    return () => {
      stopPolling();
    };
    // Intentionally runs on mount only. executionGuid is read from the module-level
    // persistedExecutionGuid at useState initialization, so it holds the correct value
    // at this point. startPolling is excluded to avoid restarting polling on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startPolling(guid) {
    let offset = 0;
    pollRef.current = setInterval(async () => {
      const result = await getBatchSqlStatus(guid, offset);
      if (!result.success) return;

      setStatus(result);

      if (result.errors?.length > 0) {
        setErrors((prev) => [...prev, ...result.errors]);
        offset = result.totalErrors;
      }

      if (!result.isRunning) {
        persistedExecutionGuid = null;
        stopPolling();
        setExecuting(false);
      }
    }, 2000);
  }

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  async function runExecution(apiCall) {
    setExecuting(true);
    setStatus(null);
    setErrors([]);

    const result = await apiCall();
    if (result.success) {
      const guid = result.executionGuid;
      setExecutionGuid(guid);
      persistedExecutionGuid = guid;
      startPolling(guid);
    } else {
      setExecuting(false);
    }
  }

  if (!sysAdmin) {
    return <Navigate to="/supportconsole" replace />;
  }

  const isComplete = status && !status.isRunning;
  const hasFailed = isComplete && status.failed > 0;
  const pct = status?.percentComplete || 0;

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="lg" PaperProps={{ sx: { overflowX: "hidden" } }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "dialogTitleBackground",
          borderBottom: "1px solid",
          borderBottomColor: "lineSeparatorMinor",
          padding: "0.5rem 1rem",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CloseX onClick={handleClose} />
        <Typography variant="h3">
          Batch SQL Update
        </Typography>
      </Box>

      {/* Content */}
      <DialogContent sx={{ pt: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Execute a SQL script across all tenant databases in parallel (one
            thread per elastic pool).
          </Typography>

          {/* Credentials */}
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <FrmTextField
              label="SQL Username"
              name="dbUsername"
              value={dbUsername}
              onValueChange={(name, value) => setDbUsername(value)}
              sx={{ width: 300 }}
            />
            <FrmTextField
              label="SQL Password"
              name="dbPassword"
              type="password"
              value={dbPassword}
              onValueChange={(name, value) => setDbPassword(value)}
              autoComplete="new-password"
              sx={{ width: 300 }}
            />
          </Box>

          {/* SQL Script */}
          <TextField
            label="SQL Script"
            multiline
            minRows={8}
            maxRows={20}
            fullWidth
            value={sqlScript}
            onChange={(e) => setSqlScript(e.target.value)}
            placeholder="Enter SQL here. GO batch separators are supported."
            InputLabelProps={{ shrink: true }}
            slotProps={{
              input: {
                sx: { fontFamily: "monospace", fontSize: "0.85rem" },
              },
            }}
            sx={{ mb: 2 }}
          />

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <Button
              variant="contained"
              onClick={() => runExecution(() => startBatchSqlUpdate(sqlScript, dbUsername, dbPassword))}
              disabled={executing || !dbUsername || !dbPassword || !sqlScript.trim()}
            >
              Execute
            </Button>
            {hasFailed && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => runExecution(() => retryFailedBatchSql(executionGuid, dbUsername, dbPassword))}
                disabled={executing || !dbUsername || !dbPassword}
              >
                Retry Failed Databases
              </Button>
            )}
          </Box>

          {/* Progress */}
          {status && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 24, borderRadius: 1, mb: 1 }}
              />
              <Typography variant="body2">
                {pct}% — Total: {status.totalDatabases} | Succeeded:{" "}
                {status.succeeded} | Failed: {status.failed} | Completed:{" "}
                {status.completed} / {status.totalDatabases}
              </Typography>
              {isComplete && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    fontWeight: "bold",
                    color:
                      status.finalStatus === "Completed"
                        ? "success.main"
                        : "warning.main",
                  }}
                >
                  {status.finalStatus === "Completed"
                    ? "Batch update completed successfully."
                    : `Completed with ${status.failed} error(s).`}
                </Typography>
              )}
            </Box>
          )}

          {/* Errors table */}
          {errors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Errors ({errors.length})
              </Typography>
              <TableContainer
                sx={{ maxHeight: 300, border: "1px solid", borderColor: "lineSeparatorMinor" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Database</TableCell>
                      <TableCell>Server</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Attempts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errors.map((err, i) => (
                      <TableRow key={i}>
                        <TableCell>{err.databaseName}</TableCell>
                        <TableCell>{err.serverFqdn}</TableCell>
                        <TableCell>{err.errorMessage}</TableCell>
                        <TableCell>{err.attemptCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
